import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {NGXLogger} from 'ngx-logger';
import {forkJoin, map, Observable, take} from 'rxjs';
import {ContextConfig, ContextConfigService} from '../config/service/context-config.service';
import {clearReloadCount} from '../privacy-policy-content/privacy-policy-reload-count';
import {derivePrivacyPolicyState} from '../privacy-policy-content/privacy-policy-state';
import {PRIVACY_POLICY_VERSION} from '../privacy-policy-content/privacy-policy-version';
import {
  EulaPopupComponent,
  EulaPopupData,
  EulaPopupResult,
} from './eula-popup/eula-popup.component';


@Injectable({
  providedIn: 'root'
})
export class EulaGuard
   {

  constructor(
    private logger: NGXLogger,
    private contextConfigService: ContextConfigService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private oidcSecurityService: OidcSecurityService,
  ) {
  }

  private showEulaApproval(
    contextConfig: ContextConfig,
    state: RouterStateSnapshot
  ) {
    // TODO: disable animation with Angular 14+
    const dialogRef = this.dialog.open<EulaPopupComponent, EulaPopupData, EulaPopupResult>(
      EulaPopupComponent,
      {
        disableClose: true,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
        data: {
          versionMatch: derivePrivacyPolicyState(contextConfig.eula).version,
        },
      },
    );

    // INFO: Der Dialog schickt die Zustimmung selbst ab; hier kommt nur noch an, wie er
    // ausgegangen ist.
    dialogRef.afterClosed()
      .subscribe(result => {
        switch (result) {
          case 'approved':
            this.logger.info('EulaGuard: approval stored, continuing');

            // info: save locally and navigate desired url
            contextConfig.eula.approved = true;
            contextConfig.eula.approvedVersion = PRIVACY_POLICY_VERSION;

            // INFO: navigateByUrl statt navigate, weil state.url eine vollständige URL ist.
            // navigate([...]) nimmt jeden Eintrag als Pfadabschnitt und kodiert ihn - aus
            // "/standalone?code=..." wurde so ein Abschnitt "/standalone%3Fcode%3D...", den die
            // Standalone-Ansicht als unbekannten App-Schlüssel auf "nicht gefunden" umleitete.
            this.router.navigateByUrl(state.url);
            break;

          case 'reauthenticate':
            this.logger.info('EulaGuard: approval unauthorized, starting a new sign-in');
            this.oidcSecurityService.authorize();
            break;

          case 'forbidden':
            this.logger.warn('EulaGuard: approval forbidden, no access');
            this.snackBar.open(
              'Dir fehlt die Berechtigung für das Just Farming Portal. Bitte wende Dich an den Benutzerservice.',
              undefined,
              {
                duration: 10000,
                panelClass: 'error'
              },
            );
            break;

          default:
            this.logger.trace('EulaGuard: dialog gone without a result');
            break;
        }
      });
  }

  private checkEulaApproval(
    contextConfig: ContextConfig,
    state: RouterStateSnapshot,
  ): boolean {
    const policyState = derivePrivacyPolicyState(contextConfig.eula);
    if (policyState.access === 'granted') {
      if (policyState.version === 'matches') {
        // INFO: Eine Freigabe bei passender Fassung belegt wie eine gespeicherte Zustimmung, dass
        // die Abweichung behoben ist.
        clearReloadCount();
      }
      // info: no approval needed, continue
      return true;
    }
    // info: show
    this.logger.trace('EulaGuard: eula approval needed');
    this.showEulaApproval(contextConfig, state);
    return false;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const contextConfig = this.contextConfigService.contextConfig;

    // INFO: shortcut: if config is already present, simply allow
    if (contextConfig) {
      return this.checkEulaApproval(contextConfig, state);
    }
    this.logger.trace('EulaGuard: context config missing, waiting');

    // INFO: trigger config load and wait for complete
    return forkJoin([
      // INFO: forkJoin only takes last value, therefore the observable has to be completed
      // since loaded$ ist a replay subject its completes by take(1)
      this.contextConfigService.loaded$.pipe(take(1)),
    ]).pipe(
      // INFO: simply map to true on completion
      map(value => {
        const contextConfig = value[0];
        return this.checkEulaApproval(contextConfig, state);
      }),
    );
  }
}
